'use client'

import { Period } from '~/app/accounting/period-utils'
import { useAccountingTokenPositions } from '~/app/accounting/useAccountingTokenPositions'
import { CurrencyAmount, Message } from '~/components/component-library'
import { Timestamp } from '~/components/component-library/Timestamp'
import { cn } from '~/utils/tailwind-utils'

export const AccountingBalanceSummary = ({
  period,
  accountIds,
  chainIds,
  tokenKeys,
  isTokensTab = false,
}: {
  period: Period | null
  accountIds?: string[]
  chainIds?: number[]
  // Scopes the totals to these tokens (see buildTokenFilterKey).
  tokenKeys?: string[]
  isTokensTab?: boolean
}) => {
  const {
    startTotal,
    endTotal,
    netChange,
    startDate,
    endDate,
    endDateIsNow,
    hasError,
    tz,
  } = useAccountingTokenPositions({ period, accountIds, chainIds, tokenKeys })

  // On the Tokens tab this summary is the only error surface (the token table
  // shares the same query and stays silent), so it speaks for both.
  if (hasError)
    return (
      <Message variant="warning">
        {isTokensTab
          ? 'Unable to load balance summary and tokens for this period'
          : 'Unable to load balance summary for this period'}
      </Message>
    )

  return (
    <div className="grid grid-cols-3 gap-4 rounded-xl bg-gray-100 p-4 dark:bg-gray-900">
      <BalanceColumn
        label="Starting balance"
        dateLabel={
          startDate ? (
            <Timestamp
              timestamp={startDate.getTime()}
              type="absolute"
              tz={tz}
              showTooltip
            />
          ) : undefined
        }
        value={startTotal}
      />
      <BalanceColumn
        label="Ending balance"
        dateLabel={
          endDateIsNow ? (
            <span className="whitespace-nowrap">
              <Timestamp
                timestamp={new Date().getTime()}
                type="absolute"
                tz={tz}
                showTooltip
              />{' '}
              (now)
            </span>
          ) : (
            <Timestamp
              timestamp={endDate.getTime()}
              type="absolute"
              tz={tz}
              showTooltip
            />
          )
        }
        value={endTotal}
      />
      <BalanceColumn
        label="Net change"
        value={netChange}
        direction={
          netChange == null ? undefined : netChange >= 0 ? 'inflow' : 'outflow'
        }
      />
    </div>
  )
}

const BalanceColumn = ({
  label,
  dateLabel,
  value,
  direction,
}: {
  label: string
  dateLabel?: React.ReactNode
  value: number | undefined
  direction?: 'inflow' | 'outflow'
}) => (
  <div className="flex w-full flex-col gap-2">
    <div className="flex items-baseline gap-1.5 text-sm font-medium">
      <span className="text-primary">{label}</span>
      {dateLabel && (
        <span className="font-normal text-secondary">{dateLabel}</span>
      )}
    </div>
    <div
      className={cn('text-xl font-medium', {
        'text-green-600': direction === 'inflow',
        'text-primary': direction !== 'inflow',
      })}
    >
      {value != null && direction === 'inflow' && '+'}
      {value != null && direction === 'outflow' && '-'}
      <CurrencyAmount
        layout="inline"
        value={value != null ? Math.abs(value) : value}
      />
    </div>
  </div>
)
